import { type RefObject, useEffect, useRef } from 'react';

const CABLE_PATH = '/cable';

const HTTPS_PROTOCOL = 'https:';

const WEBSOCKET_PROTOCOL = {
  SECURE: 'wss:',
  PLAIN: 'ws:',
} as const;

const CABLE_COMMAND = {
  SUBSCRIBE: 'subscribe',
} as const;

const CABLE_MESSAGE_TYPE = {
  REJECT_SUBSCRIPTION: 'reject_subscription',
} as const;

type ActionCableSubscription = {
  channel: string;
  params?: Record<string, unknown>;
};

const closeSocket = (wsRef: RefObject<WebSocket | null>) => {
  if (!wsRef.current) {
    return;
  }

  wsRef.current.onclose = null;
  wsRef.current.close();
  wsRef.current = null;
};

type UseActionCableOptions = {
  onMessage?: (data: Record<string, unknown>) => void;
  onDisconnect?: () => void;
};

export const useActionCable = (options: UseActionCableOptions = {}) => {
  const wsRef = useRef<WebSocket | null>(null);
  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  });

  const disconnect = () => closeSocket(wsRef);

  const subscribe = (subscription: ActionCableSubscription) => {
    disconnect();

    const protocol =
      window.location.protocol === HTTPS_PROTOCOL
        ? WEBSOCKET_PROTOCOL.SECURE
        : WEBSOCKET_PROTOCOL.PLAIN;
    const ws = new WebSocket(
      `${protocol}//${window.location.host}${CABLE_PATH}`,
    );
    wsRef.current = ws;

    ws.onopen = () => {
      const identifier = JSON.stringify({
        channel: subscription.channel,
        ...subscription.params,
      });
      ws.send(JSON.stringify({ command: CABLE_COMMAND.SUBSCRIBE, identifier }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === CABLE_MESSAGE_TYPE.REJECT_SUBSCRIPTION) {
        ws.close();
        return;
      }

      if (data.message) {
        optionsRef.current.onMessage?.(data.message);
      }
    };

    ws.onclose = () => {
      optionsRef.current.onDisconnect?.();
    };
  };

  useEffect(() => () => closeSocket(wsRef), []);

  return { subscribe, disconnect };
};
